import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type SidebarMode = "workspace" | "curso";

interface LayoutContextType {
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  currentStage: number;
  currentStep: number;
  navigateToStep: (stage: number, step: number, updateUrl?: boolean) => void;
  selectedOrganization: string | null;
  setSelectedOrganization: (org: string | null) => void;
}

const LayoutContext = createContext<LayoutContextType>({
  sidebarMode: "curso",
  setSidebarMode: () => {},
  isSidebarOpen: true,
  openSidebar: () => {},
  closeSidebar: () => {},
  toggleSidebar: () => {},
  currentStage: 0,
  currentStep: 0,
  navigateToStep: () => {},
  selectedOrganization: null,
  setSelectedOrganization: () => {},
});

// Define each hook separately for compatibility with Fast Refresh
export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (context === undefined) {
    throw new Error("useLayoutContext must be used within a LayoutProvider");
  }
  return context;
};

// Helper functions for localStorage
const getStoredValue = (key: string, defaultValue: any) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch (e) {
    console.error('Error reading from localStorage:', e);
    return defaultValue;
  }
};

const storeValue = (key: string, value: any) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error('Error writing to localStorage:', e);
  }
};

export const LayoutProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>("curso");
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [currentStage, setCurrentStage] = useState(getStoredValue('currentStage', 0));
  const [currentStep, setCurrentStep] = useState(getStoredValue('currentStep', 0));
  const [isNavigatingFromUrl, setIsNavigatingFromUrl] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  // This isFirstLoad ref ensures we properly handle the first load from URL
  const isFirstLoad = useRef(true);
  // Store the URL parameters to ensure they're preserved on refresh
  const urlParamsRef = useRef<URLSearchParams | null>(null);

  useEffect(() => {
    // Detect current stage and step from URL
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const stepParam = searchParams.get('step');
    // Store URL parameters on first load or when they change
    urlParamsRef.current = searchParams;
    
    setIsNavigatingFromUrl(true);
    let newStage = 0;
    let newStep = stepParam ? parseInt(stepParam, 10) : 1;
    
    if (pathname.includes('stage-one')) {
      newStage = 1;
    } else if (pathname.includes('stage-two')) {
      newStage = 2;
    } else if (pathname.includes('stage-three')) {
      newStage = 3;
    } else if (pathname.includes('stage-four')) {
      newStage = 4;
    } else {
      // If we're not on a stage page, get values from localStorage or reset
      newStage = getStoredValue('currentStage', 0);
      newStep = getStoredValue('currentStep', 0);
    }
    
    // IMPORTANT: Always prioritize URL parameters on both first load AND refreshes
    // This is the key fix for the refresh issue
    if (pathname.includes(`stage-${newStage}`) && stepParam) {
      console.log(`Using URL step parameter: stage=${newStage}, step=${newStep}`);
      setCurrentStage(newStage);
      setCurrentStep(newStep);
      storeValue('currentStage', newStage);
      storeValue('currentStep', newStep);
      isFirstLoad.current = false;
    } else if (isFirstLoad.current) {
      // Handle normal first load without URL parameters
      console.log(`First load without URL params: stage=${newStage}, step=${newStep || 1}`);
      setCurrentStage(newStage);
      setCurrentStep(newStep || 1);
      storeValue('currentStage', newStage);
      storeValue('currentStep', newStep || 1);
      isFirstLoad.current = false;
    } else if (newStage !== 0 && (newStage !== currentStage || newStep !== currentStep)) {
      // Normal update logic for subsequent navigation
      console.log(`Updating stage/step: stage=${newStage}, step=${newStep}`);
      setCurrentStage(newStage);
      setCurrentStep(newStep);
      
      // Store the new values in localStorage
      storeValue('currentStage', newStage);
      storeValue('currentStep', newStep);
    }

    // Set sidebar mode based on URL
    if (pathname.includes('/workspace')) {
      setSidebarMode('workspace');
    } else if (pathname.includes('/stage-') || pathname.includes('/intro')) {
      setSidebarMode('curso');
    }

    // Close sidebar on mobile by default
    const isMobile = window.innerWidth < 768;
    if (isMobile) {
      setIsSidebarOpen(false);
    }
    
    // Reset the flag after processing
    setTimeout(() => {
      setIsNavigatingFromUrl(false);
    }, 100);
  }, [location, currentStage, currentStep]);

  const navigateToStep = (stage: number, step: number, updateUrl: boolean = true) => {
    // Always update the current stage and step in the state
    setCurrentStage(stage);
    setCurrentStep(step);
    
    // Store the values in localStorage
    storeValue('currentStage', stage);
    storeValue('currentStep', step);
    
    // Only update the URL if requested (to avoid loops)
    if (updateUrl) {
      let stagePath = '';
      
      switch (stage) {
        case 1:
          stagePath = '/stage-one';
          break;
        case 2:
          stagePath = '/stage-two';
          break;
        case 3:
          stagePath = '/stage-three';
          break;
        case 4:
          stagePath = '/stage-four';
          break;
        default:
          stagePath = '/';
      }
      
      navigate(`${stagePath}?step=${step}`);
    }
  };

  const openSidebar = () => setIsSidebarOpen(true);
  const closeSidebar = () => setIsSidebarOpen(false);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  return (
    <LayoutContext.Provider
      value={{
        sidebarMode,
        setSidebarMode,
        isSidebarOpen,
        openSidebar,
        closeSidebar,
        toggleSidebar,
        currentStage,
        currentStep,
        navigateToStep,
        selectedOrganization,
        setSelectedOrganization,
      }}
    >
      {children}
    </LayoutContext.Provider>
  );
};
